import _ from "lodash";
import { action, decorate, observable, reaction } from "mobx";
import { toast } from "react-toastify";
import { VALID_FEED_TYPES } from "utils/constants";

function hasValidFeedTypes(videos) {
  return _.some(videos, v => VALID_FEED_TYPES.has(v.feedType));
}

/**
 * Function used to mutate a play object during reduce operation.
 */
function playReducer(accumulator, play) {
  if (!_.isEmpty(play.videos) && hasValidFeedTypes(play.videos)) {
    play.videos = _.filter(play.videos, v => VALID_FEED_TYPES.has(v.feedType));
    play.videos = _.orderBy(play.videos, "rank");
    play.videoSrc = play.videos[0].link;
    play.feedType = play.videos[0].feedType;
    accumulator.push(observable.map(play));
    return accumulator;
  } else {
    play.videoSrc = "";
    play.feedType = "";
    accumulator.push(observable.map(play));
    return accumulator;
  }
}

class VideoSearchStore {
  constructor(pitsApi, sportyApi, experienceStore) {
    this.pitsApi = pitsApi;
    this.sportyApi = sportyApi;
    this.experienceStore = experienceStore;

    this.getPlays = this.getPlays.bind(this);
    this.query = "";
    this.queryErrors = [];
    this.isLoading = false;
    this.feedPreference = "";

    this.plays = [];
    this.totalPlays = 0;
    this.page = 0;
    this.playsPerPage = 10;

    reaction(
      () => this.page,
      page => {
        this.getPlays();
      }
    );

    reaction(
      () => this.playsPerPage,
      playsPerPage => {
        this.page = 0;
        this.getPlays();
      }
    );

    reaction(
      () => this.experienceStore.searchType,
      () => {
        if (!this.query) {
          return;
        }
        if (this.page !== 0) {
          this.setPage(0);
        } else {
          this.getPlays();
        }
      }
    );
  }

  setQuery(q) {
    this.query = q;
  }

  setPage(page) {
    this.page = page;
  }

  setPlays(plays) {
    this.plays = plays;
  }

  setTotalPlays(num) {
    this.totalPlays = num;
  }

  setPlaysPerPage(playsPerPage) {
    this.playsPerPage = playsPerPage;
  }

  resetPagination() {
    this.page = 0;
  }

  updateActiveVideoForPlay(playId, video) {
    const play = _.find(this.plays, p => p.get("id") === playId);
    play.set("videoSrc", video.link);
    play.set("feedType", video.feedType);
  }

  setQueryErrors(errors) {
    this.queryErrors = errors;
  }

  getPlaysForQuickClip() {
    // Note: quick clips are limited to a maximum of 250 clips
    return this.pitsApi
      .search(this.query, 250, 0, undefined, this._searchType())
      .then(data => {
        return _.reduce(data.plays, playReducer, []);
      });
  }

  _searchType() {
    return this.experienceStore.searchType;
  }

  getPlays() {
    if (!this._searchType()) {
      return;
    }
    this.setQueryErrors([]);
    this.isLoading = true;
    this.pitsApi
      .search(
        this.query,
        this.playsPerPage,
        this.page,
        this.feedPreference,
        this._searchType()
      )
      .then(data => {
        if (data.total === 0) {
          this.setQueryErrors(["Oops! No results found."]);
        }
        const playsWithVideo = _.reduce(data.plays, playReducer, []);
        this.setPlays(playsWithVideo);
        this.setTotalPlays(data.total);
        this.isLoading = false;
      })
      .catch(e => {
        console.log(e);
        if (_.has(e, "graphQLErrors")) {
          const { message } = e.graphQLErrors[0];
          const error = message.split(":");
          const errorMessage = error.length > 1 ? error[1].trim() : "Error Fetching Plays.";
          toast.error(errorMessage, {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 5000
          });
        } else {
          toast.error("An unexpected error occurred. Please try again.", {
            position: toast.POSITION.TOP_CENTER,
            autoClose: 5000
          });
        }
        this.setPlays([]);
        this.setTotalPlays(0);
        this.isLoading = false;
      });
  }
}

decorate(VideoSearchStore, {
  feedPreference: observable,
  query: observable,
  queryErrors: observable,
  isLoading: observable,
  setQuery: action,
  setQueryErrors: action,
  page: observable,
  plays: observable,
  playsPerPage: observable,
  totalPlays: observable,
  setPage: action,
  setPlays: action,
  setTotalPlays: action,
  setPlaysPerPage: action,
  resetPagination: action,
  updateActiveVideoForPlay: action
});

export default VideoSearchStore;



// WEBPACK FOOTER //
// ./src/stores/VideoSearchStore.js